import React from 'react';
import { formatDateFull, formatDateTime } from '../../utils/date';
import { Modal } from '../ui/Modal';
import { H1, H2, Body } from '../ui/Typography';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Loading';
import { useObservation, useObservationMediaList } from '../../hooks/api';
import { useModal } from '../../stores/uiStore';
import { useHasPermission } from '../../stores/authStore';
import MediaGallery from './MediaGallery';

interface ObservationDetailModalProps {
  onEdit?: (observationId: string) => void;
  onDelete?: (observationId: string) => void;
  onPublish?: (observationId: string) => void;
  onUnpublish?: (observationId: string) => void;
  onVerify?: (observationId: string) => void;
}

const ObservationDetailModal: React.FC<ObservationDetailModalProps> = ({
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
  onVerify,
}) => {
  const modal = useModal('observation-detail');
  const observationId = modal.data?.observationId;
  
  const { data: observation, isLoading: isLoadingObservation } = useObservation(
    observationId,
    modal.isOpen && !!observationId
  );
  
  const { data: media = [] } = useObservationMediaList(
    observationId,
    modal.isOpen && !!observationId
  );
  
  // Permissions
  const canEditObservation = useHasPermission('observation.update');
  const canDeleteObservation = useHasPermission('observation.delete');
  const canPublishObservation = useHasPermission('observation.publish');
  const canVerifyObservation = useHasPermission('observation.verify');
  
  const handleClose = () => {
    modal.close();
  };
  
  const getCategoryVariant = (category: string) => {
    switch (category) {
      case 'academic': return 'info';
      case 'social': return 'success';
      case 'emotional': return 'warning';
      case 'physical': return 'secondary';
      default: return 'default';
    }
  };
  
  // formatDateFull is imported from utils/date
  
  if (isLoadingObservation) {
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={handleClose}
        size="lg"
      >
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Modal>
    );
  }
  
  if (!observation) {
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={handleClose}
        size="lg"
      >
        <div className="p-6 text-center">
          <Body className="text-gray-500">Observation not found</Body>
        </div>
      </Modal>
    );
  }
  
  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={handleClose}
      size="xl"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <H1 className="mb-2">
              {observation.child_name ||
                (observation.child
                  ? `${observation.child.name} ${observation.child.surname}`
                  : 'Unknown Child')}
            </H1>
            <Body className="text-gray-600">
              {formatDateFull(observation.observation_date)}
              {(observation.teacher_name || observation.teacher) && (
                <span className="ml-2">
                  by{' '}
                  {observation.teacher_name ||
                    `${observation.teacher!.first_name} ${observation.teacher!.last_name}`}
                </span>
              )}
            </Body>
          </div>
          
          {/* Status Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={getCategoryVariant(observation.category)} size="sm">
              {observation.category}
            </Badge>
            
            {observation.is_milestone && (
              <Badge variant="success" size="sm">
                Milestone
              </Badge>
            )}
            
            {observation.is_concern && (
              <Badge variant="error" size="sm">
                Concern
              </Badge>
            )}
            
            {observation.follow_up_required && (
              <Badge variant="warning" size="sm">
                Follow-up
              </Badge>
            )}
            
            <Badge 
              variant={observation.is_published ? 'success' : 'default'}
              size="sm"
            >
              {observation.is_published ? 'Published' : 'Draft'}
            </Badge>
            
            {observation.is_verified && (
              <Badge variant="info" size="sm">
                Verified
              </Badge>
            )}
            
            {observation.is_private && (
              <Badge variant="secondary" size="sm">
                Private
              </Badge>
            )}
          </div>
        </div>
        
        {/* Learning Area */}
        {observation.learning_area && (
          <div className="mb-4">
            <H2 className="text-sm font-medium text-gray-700 mb-1">Learning Area</H2>
            <Body className="text-gray-600">{observation.learning_area}</Body>
          </div>
        )}
        
        {/* Narrative */}
        <div className="mb-6">
          <H2 className="text-sm font-medium text-gray-700 mb-2">Observation</H2>
          <Body className="text-gray-900 whitespace-pre-wrap leading-relaxed">
            {observation.narrative}
          </Body>
        </div>
        
        {/* Tags */}
        {observation.tags && observation.tags.length > 0 && (
          <div className="mb-6">
            <H2 className="text-sm font-medium text-gray-700 mb-2">Tags</H2>
            <div className="flex flex-wrap gap-2">
              {observation.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" size="sm">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Media Gallery */}
        {media.length > 0 && (
          <div className="mb-6">
            <H2 className="text-sm font-medium text-gray-700 mb-3">Media Attachments</H2>
            <MediaGallery
              media={media}
              size="md"
              showCaptions={true}
            />
          </div>
        )}
        
        {/* Metadata */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Body className="font-medium text-gray-700">Created</Body>
              <Body className="text-gray-500">
                {formatDateTime(observation.created_at)}
              </Body>
            </div>
            <div>
              <Body className="font-medium text-gray-700">Last Updated</Body>
              <Body className="text-gray-500">
                {formatDateTime(observation.updated_at)}
              </Body>
            </div>
            {observation.is_verified && observation.verified_at && (
              <div>
                <Body className="font-medium text-gray-700">Verified</Body>
                <Body className="text-gray-500">
                  {formatDateTime(observation.verified_at)}
                </Body>
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
          <div className="flex flex-wrap gap-2">
            {canEditObservation && onEdit && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => onEdit(observation.id)}
              >
                Edit
              </Button>
            )}
            
            {canPublishObservation && !observation.is_published && onPublish && (
              <Button 
                variant="success" 
                size="sm"
                onClick={() => onPublish(observation.id)}
              >
                Publish
              </Button>
            )}
            
            {canPublishObservation && observation.is_published && onUnpublish && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onUnpublish(observation.id)}
              >
                Unpublish
              </Button>
            )}
            
            {canVerifyObservation && !observation.is_verified && onVerify && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => onVerify(observation.id)}
              >
                Verify
              </Button>
            )}
            
            {canDeleteObservation && onDelete && (
              <Button 
                variant="danger" 
                size="sm"
                onClick={() => onDelete(observation.id)}
              >
                Delete
              </Button>
            )}
          </div>
          
          <Button
            variant="ghost"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ObservationDetailModal;
